import React from "react";

const Skeleton = ({
  className = "",
  variant = "text",
  width,
  height,
  lines = 1,
  rounded = "rounded-xl",
  animate = true,
}) => {
  const baseClasses = `${animate ? "shimmer" : ""} ${rounded}`;

  const variants = {
    text: "h-4",
    title: "h-6",
    subtitle: "h-5",
    button: "h-10",
    card: "h-32",
    avatar: "w-10 h-10 rounded-full",
    input: "h-10",
    table: "h-12",
    list: "h-16",
  };

  const getVariantClass = () => {
    if (variant === "custom") return "";
    return variants[variant] || variants.text;
  };

  const getWidth = () => {
    if (width) return width;
    if (variant === "avatar") return "w-10";
    if (variant === "button") return "w-24";
    if (variant === "title") return "w-48";
    if (variant === "subtitle") return "w-32";
    return "w-full";
  };

  const getHeight = () => {
    if (height) return height;
    return getVariantClass();
  };

  if (lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={`${baseClasses} ${getWidth()} ${getHeight()} ${className}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${getWidth()} ${getHeight()} ${className}`}
    />
  );
};

export const SkeletonCard = () => (
  <div
    className="rounded-xl border border-[var(--color-border)] p-6 space-y-4 bg-[var(--color-surface)]"
  >
    <div className="flex items-center space-x-3">
      <Skeleton variant="avatar" />
      <div className="space-y-2 flex-1">
        <Skeleton variant="title" width="w-32" />
        <Skeleton variant="text" width="w-48" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton variant="text" width="w-full" />
      <Skeleton variant="text" width="w-3/4" />
    </div>
    <div className="flex space-x-2">
      <Skeleton variant="button" width="w-16" />
      <Skeleton variant="button" width="w-20" />
    </div>
  </div>
);

export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
  <div
    className="rounded-xl border border-[var(--color-border)] overflow-hidden bg-[var(--color-surface)]"
  >
    <div
      className="px-6 py-4 border-b border-[var(--color-border)]"
      style={{ backgroundColor: "var(--color-surface-raised)" }}
    >
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} variant="text" width="w-24" />
        ))}
      </div>
    </div>
    <div className="divide-y divide-[var(--color-border)]">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-6 py-4">
          <div className="flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} variant="text" width="w-20" />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonForm = () => (
  <div
    className="rounded-xl border border-[var(--color-border)] p-6 space-y-6 bg-[var(--color-surface)]"
  >
    <div className="space-y-2">
      <Skeleton variant="text" width="w-24" />
      <Skeleton variant="input" width="w-full" />
    </div>
    <div className="space-y-2">
      <Skeleton variant="text" width="w-32" />
      <Skeleton variant="input" width="w-full" />
    </div>
    <div className="space-y-2">
      <Skeleton variant="text" width="w-28" />
      <Skeleton variant="input" width="w-full" height="h-20" />
    </div>
    <div className="flex space-x-2">
      <Skeleton variant="button" width="w-20" />
      <Skeleton variant="button" width="w-16" />
    </div>
  </div>
);

export const SkeletonList = ({ items = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: items }).map((_, index) => (
      <div
        key={index}
        className="rounded-xl border border-[var(--color-border)] p-4 bg-[var(--color-surface)]"
      >
        <div className="flex items-center space-x-3">
          <Skeleton variant="avatar" />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="w-48" />
            <Skeleton variant="text" width="w-32" />
          </div>
          <Skeleton variant="button" width="w-16" />
        </div>
      </div>
    ))}
  </div>
);

export default Skeleton;
